/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import api from 'api'
import { HFlow, Icon, TableFooter, Text, VFlow } from 'bold-ui'
import { blue } from 'bold-ui/lib/styles/colors'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import useSession from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { TableBox } from 'components/table'
import { usePagination } from 'components/table/usePagination'
import theme from 'config/theme'
import { useFlags } from 'config/useFlagsContext'
import { useEncaminhamentosExternosEspecializadosQuery, useEncaminhamentosExternosQuery } from 'graphql/hooks.generated'
import {
  EncaminhamentoExternoEspecializadoFragment,
  EncaminhamentosExternosEspecializadosQuery,
  EncaminhamentosExternosQuery,
  EspecialidadeSisreg,
  TipoEncaminhamentoExternoEnum,
} from 'graphql/types.generated'
import { LotacaoContextModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { noop } from 'lodash'
import React, { useState } from 'react'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { LotacaoFolhaRosto } from 'view/atendimentos/detail/folha-rosto/model'
import { LotacaoAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { grupoCboVisualizarDetalhesEncaminhamentosEspecializados } from 'view/cidadao/acessos'

import { convertEncaminhamentoExternoEspecializadoToImpressao } from '../impressao/converter'
import { ImpressaoEncaminhamentoExternoInput } from '../impressao/ImpressaoEncaminhamentoExternoInput'
import {
  EncaminhamentoExternoEspecializadoModel,
  EncaminhamentoExternoEspecializadoPlanoModel,
  EncaminhamentoExternoFilterModel,
  EncaminhamentoExternoRowModel,
  tipoEncaminhamentoExternoEnumRecord,
} from '../model'
import { mergeAndFilterEncaminhamentos } from '../util/mergeAndFilterEncaminhamentos'
import EncaminhamentoExternoDetalhesPanel from './detalhes/EncaminhamentoExternoDetalhesPanel'
import EncaminhamentoExternoEspecializadoDetalhesPanel from './detalhes/EncaminhamentoExternoEspecializadoDetalhesPanel'
import { EncaminhamentoExternoFilter } from './EncaminhamentoExternoFilter'
import { EncaminhamentoExternoTableButtons } from './EncaminhamentoExternoTableButtons'
import { StatusClassificacaoRiscoEncaminhamento } from './StatusClassificacaoRiscoEncaminhamento'

export type EncaminhamentosModel = EncaminhamentosExternosEspecializadosQuery['encaminhamentosEspecializados']
export type EncaminhamentoExternoEspecializadoQueryModel = EncaminhamentoExternoEspecializadoFragment
export type EncaminhamentoExternoQueryModel = EncaminhamentosExternosQuery['encaminhamentos'][0]

export interface EncaminhamentoExternoTableProps {
  atendimentoId?: ID
  prontuarioId: ID
  dataReferencia: Instant
  lotacao: LotacaoAtendimento | LotacaoFolhaRosto | LotacaoContextModel
  encaminhamentosCache?: EncaminhamentoExternoEspecializadoPlanoModel
  atendimentoProfissionalId?: ID
  isAtendimentoObservacao?: boolean
  isAtendimentoOdonto?: boolean
  removeItem?(itemToRemove: EncaminhamentoExternoEspecializadoModel): void
  readOnly?: boolean
}

export function EncaminhamentoExternoTable(props: Readonly<EncaminhamentoExternoTableProps>) {
  const {
    atendimentoId,
    prontuarioId,
    dataReferencia,
    lotacao,
    atendimentoProfissionalId,
    encaminhamentosCache = {},
    isAtendimentoObservacao = false,
    isAtendimentoOdonto,
    removeItem = noop,
    readOnly,
  } = props

  const { handleImpressaoPath } = useHandleImpressao()

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissionalId)

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()

  const { isExpanded, handleAccordionItemClick, resetExpandedItems, ...accordionProps } = useAccordionControl({
    allowMultipleExpanded: true,
  })

  const [filters, setFilters] = useState<EncaminhamentoExternoFilterModel>({})

  const {
    data: { profissional },
    hasCboAuth,
  } = useSession()

  const mostrarDetalhesEncaminhamentoEspecializado = (row: EncaminhamentoExternoRowModel) =>
    hasCboAuth(grupoCboVisualizarDetalhesEncaminhamentosEspecializados) &&
    row?.tipoEncaminhamento === TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO

  const {
    data: { encaminhamentos },
  } = useEncaminhamentosExternosQuery({
    variables: {
      prontuarioId,
      editAtendProfId,
    },
  })

  const {
    data: { encaminhamentosEspecializados },
  } = useEncaminhamentosExternosEspecializadosQuery({
    variables: {
      prontuarioId,
      editAtendProfId,
    },
  })

  const encaminhamentosExibir: EncaminhamentoExternoRowModel[] = mergeAndFilterEncaminhamentos(
    encaminhamentosEspecializados?.content,
    encaminhamentos,
    encaminhamentosCache,
    profissional.id,
    filters,
    atendimentoProfissionalId,
    dataReferencia,
    lotacao
  )

  const especialidadesUnicas = [
    ...(encaminhamentosEspecializados?.content
      .map((item) => [item.especialidadeSisreg.id, item.especialidadeSisreg])
      .concat(
        encaminhamentosCache.encaminhamentosEspecializadosRecentes?.map((item) => [
          item.especialidadeSisreg.id,
          item.especialidadeSisreg,
        ])
      )
      .filter((item) => !!item)
      .reduce((acc, item) => acc.set(item[0], item[1]), new Map())
      ?.values() ?? []),
  ] as EspecialidadeSisreg[]

  const handleImpressaoEncaminhamento = (value: EncaminhamentoExternoRowModel) => {
    const input: ImpressaoEncaminhamentoExternoInput = value.isRegistradoAgora
      ? {
          encaminhamentoAtual: convertEncaminhamentoExternoEspecializadoToImpressao(
            encaminhamentosCache.encaminhamentosEspecializadosRecentes.find((enc) => enc._id === value.id)
          ),
          atendimentoProfissionalId: atendimentoProfissionalId,
          atendimentoId,
          prontuarioId,
        }
      : {
          id: value.id,
          atendimentoProfissionalId: value.atendimentoProfissional?.id,
          atendimentoId,
          prontuarioId,
        }

    handleImpressaoPath({
      apiPath: api.encaminhamentoExterno.imprimirEncaminhamentoExterno,
      values: input,
      file: 'Imprimir encaminhamento externo',
    })
  }

  const renderButtons = (row: EncaminhamentoExternoRowModel) =>
    mostrarDetalhesEncaminhamentoEspecializado(row) && (
      <EncaminhamentoExternoTableButtons
        encaminhamento={row}
        onImprimir={handleImpressaoEncaminhamento}
        removeItem={removeItem}
        readOnly={readOnly}
      />
    )

  const { paginatedItems, tableProps } = usePagination({
    items: encaminhamentosExibir,
    sizeOptions: [5, 10, 30],
    onChange: resetExpandedItems,
  })

  function renderAccordionPanel(row: EncaminhamentoExternoRowModel) {
    if (mostrarDetalhesEncaminhamentoEspecializado(row)) {
      if (row.isRegistradoAgora) {
        const encaminhamento = encaminhamentosCache.encaminhamentosEspecializadosRecentes?.find(
          (enc) => enc._id === row.id
        )
        return (
          <EncaminhamentoExternoEspecializadoDetalhesPanel
            isRegistradoAgora
            lotacao={lotacao}
            encaminhamento={encaminhamento}
          />
        )
      }

      return <EncaminhamentoExternoEspecializadoDetalhesPanel encaminhamentoId={row.id} isRegistradoAgora={false} />
    }

    if (row.isRegistradoAgora) {
      return <EncaminhamentoExternoDetalhesPanel isRegistradoAgora lotacao={lotacao} />
    }

    return (
      <EncaminhamentoExternoDetalhesPanel
        atendimentoProfissionalId={row.atendimentoProfissional.id}
        isRegistradoAgora={false}
      />
    )
  }

  return (
    <VFlow>
      {PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto && (
        <EncaminhamentoExternoFilter
          onChange={setFilters}
          especialidadeSelectFieldValues={especialidadesUnicas}
          filter={filters}
          dataReferencia={dataReferencia}
          isAtendimentoOdonto={isAtendimentoOdonto}
        />
      )}

      <TableBox
        header={
          (!PILOTO_VINCULACAO_CUIDADO_ENABLED || isAtendimentoOdonto) && (
            <EncaminhamentoExternoFilter
              onChange={setFilters}
              especialidadeSelectFieldValues={especialidadesUnicas}
              filter={filters}
              dataReferencia={dataReferencia}
              isAtendimentoOdonto={isAtendimentoOdonto}
            />
          )
        }
      >
        <AccordionDataTable<EncaminhamentoExternoRowModel>
          {...accordionProps}
          isExpanded={isExpanded}
          handleAccordionItemClick={handleAccordionItemClick}
          rows={paginatedItems}
          columns={[
            {
              name: 'data',
              header: 'Data do encaminhamento',
              render: renderData,
              size: 3,
            },
            {
              name: 'risco',
              header: 'Risco',
              render: renderRisco,
              style: classes.riscoColumn,
              size: 1,
            },
            {
              name: 'especialidade',
              header: 'Tipo de encaminhamento',
              render: renderTipoEncaminhamento,
              style: classes.especialidadeColumn,
              size: 4,
            },
            { name: 'status', render: renderStatus, size: 2, style: classes.statusColumn },
            { name: 'buttons', render: renderButtons, size: 2 },
          ]}
          components={{
            AccordionPanel: ({ row }) => renderAccordionPanel(row),
          }}
        />
        <TableFooter {...tableProps} style={classes.tableFooter} />
      </TableBox>
    </VFlow>
  )
}

const renderData = (row: EncaminhamentoExternoRowModel) => {
  return (
    <HFlow alignItems='center'>
      <DateTime value={row?.atendimentoProfissional?.iniciadoEm} format='DD/MM/YYYY' />
    </HFlow>
  )
}

const renderRisco = (row: EncaminhamentoExternoRowModel) => {
  return row?.classificacaoRiscoEncaminhamento ? (
    <HFlow justifyContent='center'>
      <StatusClassificacaoRiscoEncaminhamento status={row?.classificacaoRiscoEncaminhamento} />
    </HFlow>
  ) : (
    <Text>-</Text>
  )
}

const renderTipoEncaminhamento = (row: EncaminhamentoExternoRowModel) => {
  return row?.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO ? (
    <Text>{tipoEncaminhamentoExternoEnumRecord[row?.tipoEncaminhamento].descricao}</Text>
  ) : (
    <React.Fragment>
      <Text fontWeight='bold'>{tipoEncaminhamentoExternoEnumRecord.SERVICO_ESPECIALIZADO.descricao}</Text>
      <Text> - {row?.especialidadeSisreg?.descricao.capitalize()}</Text>
    </React.Fragment>
  )
}

const renderStatus = (row: EncaminhamentoExternoRowModel) => {
  return (
    row.isRegistradoAgora && (
      <HFlow alignItems='center' justifyContent='flex-end' hSpacing={0.5}>
        <Icon color={blue.c40} size={1} icon='clockOutline' />
        <span css={classes.statusText}>
          <Text
            color='primary'
            style={css`
              white-space: nowrap;
            `}
          >
            Registrado agora
          </Text>
        </span>
      </HFlow>
    )
  )
}

const classes = {
  especialidadeColumn: css`
    ${theme.breakpoints.down('lg')} {
      word-break: break-word;
    }
  `,

  statusColumn: css`
    text-align: right;
  `,

  riscoColumn: css`
    text-align: center;
  `,

  tableFooter: css`
    border-top-width: 0;
  `,

  statusText: css`
    ${theme.breakpoints.down('lg')} {
      display: none;
    }
  `,
}
